import * as React from 'react';
import FormField from '@amzn/awsui-components-console/form-field';
import Input from '@amzn/awsui-components-console/input';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import SegmentedControl from '@amzn/awsui-components-console/segmented-control';
import Select from '@amzn/awsui-components-console/select';
import { formatSyncFrequency } from '../../../utils/cronUtils';

export interface SyncFrequency {
  type: 'hourly' | 'daily' | 'weekly' | 'monthly';
  minute: number; // 0-59
  hour?: number; // 0-23 (for daily, weekly, monthly)
  dayOfWeek?: number; // 0-6 (for weekly, 0 = Sunday)
  dayOfMonth?: number | 'last'; // 1-31 or 'last' (for monthly)
}

interface SyncFrequencyEditorProps {
  value: SyncFrequency;
  onChange: (value: SyncFrequency) => void;
}

const DAYS_OF_WEEK = [
  { label: 'Sunday', value: '0' },
  { label: 'Monday', value: '1' },
  { label: 'Tuesday', value: '2' },
  { label: 'Wednesday', value: '3' },
  { label: 'Thursday', value: '4' },
  { label: 'Friday', value: '5' },
  { label: 'Saturday', value: '6' },
];

const DAY_OF_MONTH_OPTIONS = [
  { label: 'Last day of month', value: 'last' },
  ...Array.from({ length: 31 }, (_, i) => ({
    label: `Day ${i + 1}`,
    value: String(i + 1),
  })),
];

const SyncFrequencyEditor: React.FC<SyncFrequencyEditorProps> = ({ value, onChange }) => {
  const updateSyncFrequency = (updates: Partial<SyncFrequency>) => {
    onChange({ ...value, ...updates });
  };

  return (
    <FormField label="Sync Frequency" description={formatSyncFrequency(value)}>
      <SpaceBetween size="m">
        <SegmentedControl
          selectedId={value.type}
          onChange={({ detail }) => {
            const newType = detail.selectedId as 'hourly' | 'daily' | 'weekly' | 'monthly';
            const baseUpdate: Partial<SyncFrequency> = {
              type: newType,
              minute: value.minute || 0,
            };

            // Set defaults based on type
            if (newType === 'hourly') {
              updateSyncFrequency({ ...baseUpdate, hour: undefined, dayOfWeek: undefined, dayOfMonth: undefined });
            } else if (newType === 'daily') {
              updateSyncFrequency({ ...baseUpdate, hour: value.hour || 0, dayOfWeek: undefined, dayOfMonth: undefined });
            } else if (newType === 'weekly') {
              updateSyncFrequency({ ...baseUpdate, hour: value.hour || 0, dayOfWeek: value.dayOfWeek || 0, dayOfMonth: undefined });
            } else if (newType === 'monthly') {
              updateSyncFrequency({ ...baseUpdate, hour: value.hour || 0, dayOfWeek: undefined, dayOfMonth: value.dayOfMonth || 1 });
            }
          }}
          options={[
            { text: 'Hourly', id: 'hourly' },
            { text: 'Daily', id: 'daily' },
            { text: 'Weekly', id: 'weekly' },
            { text: 'Monthly', id: 'monthly' },
          ]}
        />

        {/* Hourly: Minute only */}
        {value.type === 'hourly' && (
          <FormField label="Minute" description="Minutes past the hour (0-59)">
            <Input
              type="number"
              value={String(value.minute)}
              onChange={({ detail }) => {
                const val = parseInt(detail.value) || 0;
                updateSyncFrequency({ minute: Math.max(0, Math.min(59, val)) });
              }}
              placeholder="0"
            />
          </FormField>
        )}

        {/* Daily: Hour + Minute */}
        {value.type === 'daily' && (
          <SpaceBetween size="s" direction="horizontal">
            <FormField label="Hour" description="Hour (0-23)">
              <Input
                type="number"
                value={String(value.hour || 0)}
                onChange={({ detail }) => {
                  const val = parseInt(detail.value) || 0;
                  updateSyncFrequency({ hour: Math.max(0, Math.min(23, val)) });
                }}
                placeholder="0"
              />
            </FormField>
            <FormField label="Minute" description="Minute (0-59)">
              <Input
                type="number"
                value={String(value.minute)}
                onChange={({ detail }) => {
                  const val = parseInt(detail.value) || 0;
                  updateSyncFrequency({ minute: Math.max(0, Math.min(59, val)) });
                }}
                placeholder="0"
              />
            </FormField>
          </SpaceBetween>
        )}

        {/* Weekly: Hour + Minute + Day of Week */}
        {value.type === 'weekly' && (
          <SpaceBetween size="s">
            <FormField label="Day of Week">
              <Select
                selectedOption={DAYS_OF_WEEK.find((d) => d.value === String(value.dayOfWeek || 0)) || DAYS_OF_WEEK[0]}
                onChange={({ detail }) => updateSyncFrequency({ dayOfWeek: parseInt(detail.selectedOption.value || '0') })}
                options={DAYS_OF_WEEK}
              />
            </FormField>
            <SpaceBetween size="s" direction="horizontal">
              <FormField label="Hour" description="Hour (0-23)">
                <Input
                  type="number"
                  value={String(value.hour || 0)}
                  onChange={({ detail }) => {
                    const val = parseInt(detail.value) || 0;
                    updateSyncFrequency({ hour: Math.max(0, Math.min(23, val)) });
                  }}
                  placeholder="0"
                />
              </FormField>
              <FormField label="Minute" description="Minute (0-59)">
                <Input
                  type="number"
                  value={String(value.minute)}
                  onChange={({ detail }) => {
                    const val = parseInt(detail.value) || 0;
                    updateSyncFrequency({ minute: Math.max(0, Math.min(59, val)) });
                  }}
                  placeholder="0"
                />
              </FormField>
            </SpaceBetween>
          </SpaceBetween>
        )}

        {/* Monthly: Hour + Minute + Day of Month */}
        {value.type === 'monthly' && (
          <SpaceBetween size="s">
            <FormField label="Day of Month" description="If a specific day doesn't exist in a month, it will run on the last day">
              <Select
                selectedOption={DAY_OF_MONTH_OPTIONS.find((d) => d.value === String(value.dayOfMonth)) || DAY_OF_MONTH_OPTIONS[1]}
                onChange={({ detail }) => {
                  const val = detail.selectedOption.value === 'last' ? 'last' : parseInt(detail.selectedOption.value || '1');
                  updateSyncFrequency({ dayOfMonth: val });
                }}
                options={DAY_OF_MONTH_OPTIONS}
                filteringType="auto"
              />
            </FormField>
            <SpaceBetween size="s" direction="horizontal">
              <FormField label="Hour" description="Hour (0-23)">
                <Input
                  type="number"
                  value={String(value.hour || 0)}
                  onChange={({ detail }) => {
                    const val = parseInt(detail.value) || 0;
                    updateSyncFrequency({ hour: Math.max(0, Math.min(23, val)) });
                  }}
                  placeholder="0"
                />
              </FormField>
              <FormField label="Minute" description="Minute (0-59)">
                <Input
                  type="number"
                  value={String(value.minute)}
                  onChange={({ detail }) => {
                    const val = parseInt(detail.value) || 0;
                    updateSyncFrequency({ minute: Math.max(0, Math.min(59, val)) });
                  }}
                  placeholder="0"
                />
              </FormField>
            </SpaceBetween>
          </SpaceBetween>
        )}
      </SpaceBetween>
    </FormField>
  );
};

export default SyncFrequencyEditor;
